// Shared per-URL security-tool actions (403 bypass, CSRF PoC, upload, endpoint
// extraction, prototype pollution, SSO tampering, copy-as-cURL). Used by the
// site-tree right-click menu so it matches the traffic-list tools. Each function
// handles its own toast lifecycle and returns the server payload (or null).
import axios from '../config/axiosConfig';
import { toast } from 'react-toastify';

export const run403Bypass = async ({ webTargetId, method = 'GET', url, authProfileId }) => {
  const t = toast.loading('Testing 403/401 bypass techniques…');
  try {
    const { data } = await axios.post('/web-findings/403-bypass', { webTargetId, method, url, authProfileId });
    if (!data?.baseline?.denied) {
      toast.update(t, { render: `Baseline is ${data?.baseline?.status} (not 401/403) — nothing to bypass.`, type: 'info', isLoading: false, autoClose: 5000 });
    } else if (data.bypasses?.length) {
      toast.update(t, { render: `⚠️ ${data.bypasses.length} bypass(es) found! Finding created (${data.tested} techniques tried).`, type: 'error', isLoading: false, autoClose: 8000 });
    } else {
      toast.update(t, { render: `No bypass found (${data.tested} techniques tried, baseline ${data.baseline.status}).`, type: 'success', isLoading: false, autoClose: 6000 });
    }
    return data;
  } catch (err) {
    toast.update(t, { render: 'Bypass test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    return null;
  }
};

// The exchange-based tools accept either { exchangeId } or { webTargetId, url }.
export const runCsrfPoc = async (target) => {
  const t = toast.loading('Generating CSRF PoC…');
  try {
    const { data } = await axios.post('/web-findings/csrf-poc', target);
    const blob = new Blob([data.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    try { await navigator.clipboard.writeText(data.html); } catch { /* clipboard may be blocked */ }
    toast.update(t, { render: `CSRF PoC generated (${data.technique}) — opened in a new tab and copied to clipboard.`, type: 'success', isLoading: false, autoClose: 6000 });
    return data;
  } catch (err) {
    toast.update(t, { render: 'CSRF PoC failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    return null;
  }
};

export const runUploadScan = async (target) => {
  const t = toast.loading('Testing file-upload restrictions…');
  try {
    const { data } = await axios.post('/web-findings/upload-scan', target);
    if (data?.note && !data?.findings?.length) {
      toast.update(t, { render: data.note, type: 'info', isLoading: false, autoClose: 6000 });
    } else if (data?.findings?.length) {
      const crit = data.findings.some(f => f.severity === 'critical');
      toast.update(t, { render: `${crit ? '🔴 RCE!' : '⚠️'} Upload bypass: ${data.findings.map(f => f.title).join('; ')}`, type: 'error', isLoading: false, autoClose: 10000 });
    } else {
      const n = (data?.tests || []).length;
      toast.update(t, { render: `No upload bypass detected (${n} variants tried).`, type: 'success', isLoading: false, autoClose: 6000 });
    }
    return data;
  } catch (err) {
    toast.update(t, { render: 'Upload test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    return null;
  }
};

export const runExtractEndpoints = async (target) => {
  const t = toast.loading('Extracting endpoints from response…');
  try {
    const { data } = await axios.post('/web-findings/extract-endpoints', target);
    if (!data?.total) {
      toast.update(t, { render: data?.note || 'No endpoints found in this response.', type: 'info', isLoading: false, autoClose: 5000 });
    } else {
      toast.update(t, { render: `Found ${data.total} endpoint(s)${data.sensitiveCount ? ` (${data.sensitiveCount} sensitive)` : ''} — finding created. See Findings tab.`, type: data.sensitiveCount ? 'warning' : 'success', isLoading: false, autoClose: 7000 });
    }
    return data;
  } catch (err) {
    toast.update(t, { render: 'Endpoint extraction failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    return null;
  }
};

export const runPrototypePollution = async (target) => {
  const t = toast.loading('Testing server-side prototype pollution…');
  try {
    const { data } = await axios.post('/web-findings/prototype-pollution', target);
    if (data?.findings?.length) {
      toast.update(t, { render: `⚠️ Prototype pollution detected: ${data.findings.map(f => f.title).join('; ')}`, type: 'error', isLoading: false, autoClose: 9000 });
    } else if (data?.note) {
      toast.update(t, { render: data.note, type: 'info', isLoading: false, autoClose: 6000 });
    } else {
      const surfaces = (data?.tests || []).map(x => x.surface).join(', ') || 'tests';
      toast.update(t, { render: `No prototype pollution detected (${surfaces}).`, type: 'success', isLoading: false, autoClose: 6000 });
    }
    return data;
  } catch (err) {
    toast.update(t, { render: 'Prototype pollution test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    return null;
  }
};

export const runSsoAttack = async (target) => {
  const t = toast.loading('Running SAML/OAuth tampering tests…');
  try {
    const { data } = await axios.post('/web-findings/sso-attack', target);
    if (data?.note) {
      toast.update(t, { render: data.note, type: 'info', isLoading: false, autoClose: 6000 });
    } else if (data?.findings?.length) {
      toast.update(t, { render: `⚠️ SSO vulnerability found: ${data.findings.map(f => f.title).join('; ')}`, type: 'error', isLoading: false, autoClose: 9000 });
    } else {
      const ran = (data?.tests || []).map(x => x.name).join(', ') || 'tests';
      toast.update(t, { render: `No SSO bypass detected (${ran}).`, type: 'success', isLoading: false, autoClose: 6000 });
    }
    return data;
  } catch (err) {
    toast.update(t, { render: 'SSO test failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    return null;
  }
};

export const runBeaconInjection = async (target) => {
  const t = toast.loading('Running beacon injection scan...');
  try {
    const { data } = await axios.post('/web-findings/beacon-injection', target);
    if (data?.findings?.length) {
      toast.update(t, { render: `${data.findings.length} beacon reflection(s) found! Check beacon logs for OOB hits.`, type: 'warning', isLoading: false, autoClose: 9000 });
    } else {
      toast.update(t, { render: data?.note || `${data?.summary?.totalSent || 0} payloads sent. Monitor beacon logs.`, type: 'info', isLoading: false, autoClose: 7000 });
    }
    return data;
  } catch (err) {
    toast.update(t, { render: 'Beacon injection failed: ' + (err.response?.data?.error || err.message), type: 'error', isLoading: false, autoClose: 6000 });
    return null;
  }
};

export const buildCurl = (exchange) => {
  const method = exchange?.request?.method || 'GET';
  const url = exchange?.request?.url || '';
  let cmd = `curl -X ${method} '${url}'`;
  (exchange?.request?.headers || []).forEach(h => {
    if (String(h.name).toLowerCase() === 'content-length') return;
    cmd += ` -H '${h.name}: ${h.value}'`;
  });
  if (exchange?.request?.body) {
    const body = String(exchange.request.body).replace(/'/g, `'\\''`);
    cmd += ` --data '${body}'`;
  }
  return cmd;
};
